"use client";

import React from "react";
import { cn } from "@/lib/utils";
import PrimaryButton, {
  PrimaryButtonProps,
} from "@/components/ui/primary-button";
import SecondaryButton, {
  SecondaryButtonProps,
} from "@/components/ui/secondary-button";
import Image, { ImageProps } from "next/image";
import HeaderImage from "@/assets/header-image.jpeg";
import { useWindowSize } from "@/hooks/use-window-size";

export type HeaderBackgroundColor = "white" | "orange-light";

export interface HeaderCareersProps {
  id?: string;
  className?: string;
  headingClassName?: string;
  subheadingClassName?: string;
  heading?: string;
  subheading?: string;
  description?: string;
  legacyButton?: boolean;
  showPrimaryCTA?: boolean;
  primaryCTA?: PrimaryButtonProps;
  secondaryCTA?: SecondaryButtonProps;
  showSecondaryCTA?: boolean;
  image?: ImageProps;
  backgroundColor?: HeaderBackgroundColor;
}

const HeaderCareers: React.FC<HeaderCareersProps> = ({
  id = "header-careers",
  className,
  headingClassName = "",
  subheadingClassName = "",
  heading = "Heading",
  subheading = "Subheading",
  description = "",
  legacyButton = false,
  showPrimaryCTA = true,
  primaryCTA = {
    color: "orange",
    className: "w-fit",
    children: "Apply Now",
    href: "#",
  },
  showSecondaryCTA = true,
  secondaryCTA = {
    className: "w-fit",
    color: "teal",
    children: "Open Positions",
    href: "#",
  },
  image = {
    src: HeaderImage,
    alt: "Header Image",
  },
  backgroundColor = "white",
}) => {
  const size = useWindowSize();

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "flex w-full flex-col",
        backgroundColor === "white" ? "bg-white" : "bg-primary-100",
        className
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-4 md:gap-6 lg:flex-row lg:gap-8",
          "mx-auto flex w-full max-w-lg flex-col gap-2 p-4 md:gap-4 md:px-6 md:py-8 lg:gap-6 lg:px-3 lg:py-10"
        )}
      >
        <div className="flex w-full flex-col gap-4 md:gap-6 lg:basis-[627px] lg:flex-row lg:gap-8">
          <div className="flex flex-col gap-4 lg:w-[624px] lg:gap-6">
            <hgroup className="flex flex-col gap-2 text-center md:gap-3 md:text-start lg:gap-4">
              <h1
                className={cn(
                  "font-montserrat text-heading-3 text-secondary-500 md:text-heading-2 lg:text-heading-1",
                  headingClassName
                )}
              >
                {heading}
              </h1>
              <p
                className={cn(
                  "font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2 lg:text-subheading-1",
                  subheadingClassName
                )}
              >
                {subheading}
              </p>
            </hgroup>
            {description.replace(/<[^>]*>/g, "").trim() && (
              <p className="text-center font-roboto text-body-1-300 text-neutral-500 md:text-start">
                {typeof description === "string" ? (
                  <div
                    className="rich-text [&_a]:hover:text-primary-700 [&_a]:focus:text-primary-700 [&_a]:rounded-md [&_a]:font-roboto [&_a]:text-body-1-400 [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:no-underline [&_a]:focus:no-underline [&_a]:focus:outline [&_a]:focus:outline-1 [&_a]:focus:outline-offset-2 [&_a]:focus:outline-black"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                ) : (
                  description
                )}
              </p>
            )}
            {(!legacyButton || size === "lg") && (
              <div className={cn("mx-auto md:mx-0")}>
                <ButtonGroup
                  showPrimaryCTA={showPrimaryCTA}
                  primaryCTA={primaryCTA}
                  showSecondaryCTA={showSecondaryCTA}
                  secondaryCTA={secondaryCTA}
                />
              </div>
            )}
          </div>
        </div>
        {image && (
          <div
            className={cn(
              "relative order-none h-full basis-auto justify-center overflow-clip rounded-3xl shadow-[4px_4px_0px_0px_#FFD5BF] md:shadow-[6px_6px_0px_0px_#FFD5BF] lg:order-none lg:shadow-[8px_8px_0px_0px_#FFD5BF]",
              "mx-auto min-h-[200px] w-full flex-1 flex-col bg-center md:min-h-[400px] lg:h-auto"
            )}
          >
            {image && (
              <Image
                className="object-cover"
                {...image}
                alt={image?.alt}
                fill
                priority
              />
            )}
          </div>
        )}
        {legacyButton && size !== "lg" && (
          <div className={cn("mx-auto md:mx-0")}>
            <ButtonGroup
              showPrimaryCTA={showPrimaryCTA}
              primaryCTA={primaryCTA}
              showSecondaryCTA={showSecondaryCTA}
              secondaryCTA={secondaryCTA}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeaderCareers;

const ButtonGroup: React.FC<{
  showPrimaryCTA: boolean;
  primaryCTA: PrimaryButtonProps;
  showSecondaryCTA: boolean;
  secondaryCTA: SecondaryButtonProps;
}> = ({ showPrimaryCTA, primaryCTA, showSecondaryCTA, secondaryCTA }) => (
  <div className="flex flex-row items-center gap-3 md:gap-4 lg:gap-6">
    {showPrimaryCTA && primaryCTA && (
      <PrimaryButton
        color="orange"
        size="md"
        {...primaryCTA}
        id={primaryCTA.id ?? "primary-cta-subpage-header"}
        onClick={(e) => {
          if (primaryCTA.href?.startsWith("#")) {
            e.preventDefault();
            const element = document.querySelector(primaryCTA.href);
            if (element) {
              const offset = 80;
              const elementPosition =
                element.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({
                top: elementPosition - offset,
                behavior: "smooth",
              });
            }
          }
        }}
      />
    )}
    {showSecondaryCTA && secondaryCTA && (
      <SecondaryButton
        color="teal"
        size="md"
        {...secondaryCTA}
        onClick={(e) => {
          if (secondaryCTA.href?.startsWith("#")) {
            e.preventDefault();
            const element = document.querySelector(secondaryCTA.href);
            if (element) {
              const offset = 80;
              const elementPosition =
                element.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({
                top: elementPosition - offset,
                behavior: "smooth",
              });
            }
          }
        }}
        id={secondaryCTA.id ?? "secondary-cta-subpage-header"}
      />
    )}
  </div>
);
